import { formatTimeSpan, formDataToObject, GestCard, getAuthErrorMessage, getCookie, setSession, setSnackbar, UserData_model } from '@shared/library';
import { languages } from '@shared/library/dist/static/language';
import axios from 'axios';
import React from 'react';
import { decodeToken } from 'react-jwt';
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
import importer from '../../../helpers/importer';
import './LogIn.scss';
//ICONS
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
//#region COMPONENTS
import Alert from '@mui/material/Alert';
import Title from '@shared/library/dist/components/gest/Title/Title';
import {
    Caption,
    CustomButton,
    CustomCheckbox,
    CustomInput,
} from '../../_gest';
//#endregion
//MODELS 
import SessionModel from '@shared/library/dist/models/Session_model';
import Datetime from '../../../helpers/datetime';
//CONTEXTS
import { CntxConfig } from '../../WebGest';

export const formDataConfig = { headers: { "Content-Type": "multipart/form-data" } };

const LogIn = () => {
    //CONTEXTS USE 
    const cntxConfig = React.useContext(CntxConfig)!;

    //STATIC DATA
    const navigate = useNavigate();
    const lang = languages(cntxConfig.settings?.lang);

    //#region STATES
    const [timer, setTimer] = React.useState(0);
    const [challenge, setChallenge] = React.useState<string>();
    const twoFactorCredentialsStore = React.useRef<{ username: string; password: string; }>(undefined);

    const loginForm = React.useRef<HTMLFormElement>(null);

    //FLAGS
    const [errorAnimation, setErrorAnimation] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [askEmailCode, setAskEmailCode] = React.useState(false);
    // const [error, setError] = React.useState<boolean|string>(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    const saveSession = (token: string) => {
        localStorage.clear();

        const userData = decodeToken<UserData_model>(token);    // decodedToken
        const session: SessionModel = { userData, support: false, cancelApiConnection: axios.CancelToken.source() };
        setSession(cntxConfig, session);

        supportLogInAPI(session, token);
    };

    const supportLogInAPI = (session: SessionModel, token: string) => {
        if (session.userData?.email && session.userData?.role !== 'admin') {
            axios.get(`${config.platforms.support}/WebGESTLogin.php`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(() => updateSupportSession(true, session))
                .catch(() => { });
        }
        else if (session.userData?.role === 'admin') {
            updateSupportSession(true, session);
        }
    };

    const loginAPI = (e: React.SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (checkForTimer()) return;

        const creds = formDataToObject<{ username: string; password: string; }>(loginForm.current);

        if (creds?.username && creds?.password && creds.password.length >= 8) {
            twoFactorCredentialsStore.current = undefined;

            setLoading(true);
            axios.post<{ token: string; } | { challenge: string; }>(`${config.api.internal}/Auth/Login`, creds, { withCredentials: true })
                .then(res => {
                    const data = res.data;

                    if (data) {
                        if ("token" in data) {
                            saveSession(data.token);
                        } else {
                            setAskEmailCode(true);
                            twoFactorCredentialsStore.current = creds;
                            setChallenge(data.challenge);
                        }
                    }
                    else {
                        animateError();
                    }
                })
                .catch((error: unknown) => {
                    animateError();
                    setSnackbar(cntxConfig, { message: getAuthErrorMessage(error), severity: "error" });
                    localStorage.clear();
                })
                .finally(() => setLoading(false));
        }
        else {
            animateError();
            setSnackbar(cntxConfig, { message: "Inserisci username e password (almeno 8 caratteri)", severity: "warning" });
        }
    };

    const twoFactorAPI = (e: React.SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (twoFactorCredentialsStore.current === null) return;

        const form = formDataToObject<{ code: string; }>(loginForm.current);

        if (form?.code.length === 6 && challenge && twoFactorCredentialsStore.current?.password) {
            setLoading(true);
            axios.post<{ token: string; }>(`${config.api.internal}/Auth/TwoFactor`, { otp: form.code, challenge, password: twoFactorCredentialsStore.current.password }, { withCredentials: true })
                .then(res => saveSession(res.data.token))
                .catch((error: unknown) => {
                    animateError();
                    setSnackbar(cntxConfig, { message: getAuthErrorMessage(error), severity: "error" });
                    localStorage.clear();
                })
                .finally(() => setLoading(false));
        }
        else {
            animateError();
            setSnackbar(cntxConfig, { message: "Inserisci il codice di 6 cifre", severity: "warning" });
        }
    };
    //#endregion

    const updateSupportSession = (isActive: boolean, conf: SessionModel) => {
        localStorage.setItem('support', isActive ? '1' : "0");
        setSession(cntxConfig, { ...conf, support: isActive });
    };

    const animateError = () => {
        setErrorAnimation(true);
        setTimeout(() => setErrorAnimation(false), 100);
    };

    const checkForTimer = () => {
        const timer = getCookie("D_TIMER");
        const now = new Date();
        if (timer !== null) {
            const time_number = Number(timer);
            const time = new Date(time_number);
            if (time > now) {
                const seconds = Math.floor(time_number / 1000) - new Datetime().getUnixTimestamp();
                setTimer(seconds);
                return true;
            }
        }
        return false;
    };
    //#endregion

    //#region STATES CHANGE
    React.useEffect(() => { checkForTimer(); }, [errorAnimation]);

    React.useEffect(() => {
        if (timer > 0) {
            setTimeout(() => setTimer(timer - 1), 1000);
        }
    }, [timer]);
    //#endregion

    return (
        <GestCard type="floating" id="Login" style={{ width: '350px' }}>
            <div className="logo">
                <img className={`mb-3 ${loading ? 'spin' : ''} ${errorAnimation ? 'shake' : ''} `} src={importer.img.require('Logo.svg')} alt="" />
                <h4 className='mb-0'>{lang.log_in_into_your_account}</h4>
            </div>

            <div className='my-4'>
                {timer > 0 &&
                    <div className='mb-2 text-danger'>
                        {lang.you_can_try_login_again_in}
                        <b>{formatTimeSpan(timer)}</b>
                    </div>
                }

                <form ref={loginForm} onSubmit={!askEmailCode ? loginAPI : twoFactorAPI}>
                    {!askEmailCode ?
                        <>
                            <fieldset>
                                <CustomInput name="username" variant="cloud" img={importer.ic.require('user.svg')} placeholder={lang.username} class="mb-2" disabled={timer > 0} />
                                <CustomInput name="password" variant="cloud" minLength={8} type="password" img={importer.ic.require('password.svg')} placeholder={lang.password} showable={true} class="mb-2" disabled={timer > 0} />
                                <CustomCheckbox name="stay_logged_in" label={lang.stay_logged_in} />
                            </fieldset>

                            <div>
                                <CustomButton variant="primary" type={"submit"} class="w-100 mb-2">{lang.login}</CustomButton>
                                <a onClick={() => { navigate("/forgotPassword"); }}><strong>{lang.forgot_password}</strong></a>
                            </div>
                        </>
                        :
                        <>
                            <Alert icon={false}>
                                <Title>{lang.device_not_registered}</Title>

                                <div className='my-1 w-100 justify-content-center'>
                                    <EmailRoundedIcon fontSize='large' />
                                </div>

                                <Caption>{lang.check_email_for_code}</Caption>
                            </Alert>

                            <fieldset>
                                <CustomInput name="code" pattern="^\d+$" variant="cloud" img={importer.ic.require('password.svg')} placeholder={lang.code} class="mb-2" maxLength={6} />
                            </fieldset>

                            <div>
                                <CustomButton variant="primary" type={"submit"} class="w-100 mb-2">{lang.login}</CustomButton>
                                <a onClick={() => { navigate("/forgotPassword"); }}><strong>{lang.forgot_password}</strong></a>
                            </div>
                        </>
                    }
                </form>
            </div>
        </GestCard>
    );
};
export default React.memo(LogIn);